import React, { useState } from 'react';
import { Trash2, Users, FileSpreadsheet } from 'lucide-react'; 
import ExportReportModal from './ExportReportModal'; 

const RecordsTable = ({ 
    records, 
    collectors, 
    filterCollector, 
    setFilterCollector, 
    searchQuery,
    setSearchQuery,
    onRecordSelect, 
    loading,
    selectedRecords,
    onSelectionChange,
    onSelectAll,
    onDeleteSelected,
    onReassign,
    userRole,
    api 
}) => {
    const allVisibleSelected = records.length > 0 && records.every(r => selectedRecords.includes(r._id));
    const [reassignCollector, setReassignCollector] = useState('');
    const [isExportModalOpen, setIsExportModalOpen] = useState(false); 

    const handleReassign = () => {
        if (reassignCollector) {
            onReassign(reassignCollector);
            setReassignCollector('');
        }
    };
    
    const normalizedUserRole = (userRole || '').toLowerCase().replace(/[\s-]/g, '_');
    const canBulkAction = normalizedUserRole === 'admin' || normalizedUserRole === 'super_admin';

    // --- NEW: Sorting Logic to prioritize Exact & Numeric Ref ID Matches ---
    // Handles cases like "a-00001", "01", "0001" etc.
    let sortedRecords = [...records];
    
    if (searchQuery && records.length > 0) {
        const lowerQuery = searchQuery.toLowerCase().trim();
        const queryNum = parseInt(searchQuery, 10);
        const isNumericQuery = !isNaN(queryNum);

        sortedRecords.sort((a, b) => {
            const refA = (a.referenceId || '').toLowerCase();
            const refB = (b.referenceId || '').toLowerCase();

            // Helper function to assign a "match score" (Lower score = higher priority)
            const getScore = (ref) => {
                if (!ref) return 100;

                // 1. Exact string match (Highest Priority)
                // e.g. Search "a-00001" matches "a-00001"
                if (ref === lowerQuery) return 1;

                // 2. Numeric match (High Priority)
                // e.g. Search "1", "01", "0001" matches "A-000001"
                if (isNumericQuery) {
                    const parts = ref.split('-');
                    // Check if the number part matches exactly (ignoring letter prefix)
                    if (parts.length > 1 && parseInt(parts[1], 10) === queryNum) {
                        return 2;
                    }
                }

                // 3. Ends With match (Medium Priority)
                // e.g. Search "001" matches "A-000001"
                if (ref.endsWith(lowerQuery)) return 3;

                // 4. Starts With / Contains (Standard Priority)
                if (ref.startsWith(lowerQuery)) return 4;
                if (ref.includes(lowerQuery)) return 5;

                return 100; // No special Ref ID match
            };

            const scoreA = getScore(refA);
            const scoreB = getScore(refB);

            // Sort by score ascending
            return scoreA - scoreB;
        });
    }
    // -----------------------------------------------------------

    return (
        <div className="bg-white rounded-lg shadow-md p-6 overflow-x-auto">
            {/* PASS collectors PROP HERE */}
            <ExportReportModal 
                isOpen={isExportModalOpen} 
                onClose={() => setIsExportModalOpen(false)} 
                api={api}
                collectors={collectors} 
            />

            <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
            {/* ... (Rest of your existing code remains exactly the same) ... */}
              <div className="flex items-center gap-4 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-800">All Records</h2>
                {canBulkAction && selectedRecords && selectedRecords.length > 0 && (
                    <>
                        <button
                            onClick={onDeleteSelected}
                            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete ({selectedRecords.length})</span>
                        </button>
                        <div className="flex items-center gap-2">
                            <select
                                value={reassignCollector}
                                onChange={(e) => setReassignCollector(e.target.value)}
                                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            >
                                <option value="">Reassign to...</option>
                                {collectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                            </select>
                            <button
                                onClick={handleReassign}
                                disabled={!reassignCollector}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:bg-gray-400"
                            >
                                <Users className="w-4 h-4" />
                                <span>Reassign</span>
                            </button>
                        </div>
                    </>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                  {canBulkAction && (
                      <button
                          onClick={() => setIsExportModalOpen(true)}
                          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition-colors"
                      >
                          <FileSpreadsheet className="w-4 h-4" />
                          <span>Generate Report</span>
                      </button>
                  )}

                  <div>
                      <label htmlFor="search-records" className="sr-only">Search Records</label>
                      <input
                          type="text"
                          id="search-records"
                          // --- Updated Placeholder ---
                          placeholder="Search by Ref ID, Provider, Patient..."
                          // --------------------------
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      />
                  </div>
                  <div>
                      <label htmlFor="filter-collector" className="sr-only">Filter by Collector</label>
                      <select
                          id="filter-collector"
                          value={filterCollector}
                          onChange={(e) => setFilterCollector(e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                          <option value="">All Collectors</option>
                          {collectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                      </select>
                  </div>
              </div>
            </div>

            {loading ? <p>Loading records...</p> : (
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                onChange={onSelectAll}
                                checked={allVisibleSelected}
                                disabled={!records || records.length === 0}
                            />
                        </th>
                        {/* --- NEW REF ID COLUMN --- */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref ID</th>
                        {/* ------------------------- */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adj Number</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Collector</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {/* --- USE SORTED RECORDS HERE --- */}
                {sortedRecords && sortedRecords.length > 0 ? (
                    sortedRecords.map((record) => (
                      <tr key={record._id} className={selectedRecords && selectedRecords.includes(record._id) ? 'bg-indigo-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                           <input
                                type="checkbox"
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                checked={selectedRecords ? selectedRecords.includes(record._id) : false}
                                onChange={() => onSelectionChange(record._id)}
                            />
                        </td>
                        {/* --- NEW REF ID CELL --- */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                            {record.referenceId || '-'}
                        </td>
                        {/* ----------------------- */}
                        <td className="px-6 py-4 whitespace-nowrap">{record.provider}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{record.ptName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{record?.adjNumber?.[0]?.value || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">${record.bill?.toLocaleString() || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{record.caseStatus || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{record.assignedCollector?.username || 'Unassigned'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <button onClick={() => onRecordSelect(record)} className="text-indigo-600 hover:text-indigo-900 font-semibold">Details</button>
                        </td>
                      </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="9" className="text-center py-4">No records found.</td>
                    </tr>
                )}
                </tbody>
            </table>
            )}
        </div>
    );
};

export default RecordsTable;